import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { IConfig } from '@cian/config/shared';
import { ConsultantWidgetDesktop } from '@cian/newbuilding-chat-component';

import { toggleChat } from '../../actions/consultantChat';
import { useWebimProps } from '../../hooks/webim';
import { CHAT_OPEN_DELAY, CHAT_FORCE_OPEN_KEY, CHAT_FORCE_OPEN_INTERVAL } from '../../constants/consultantChat';
import { getBuilderChatOpened } from '../../selectors/builderChat';
import {
  accountNameSelector,
  chatOpenSelector,
  needForceOpenSelector,
  selectConsultantMessageButtonAvailable,
} from '../../selectors/consultantChat';
import { isNeedToShow, updateShownTimestamp } from '../../utils/consultantChat/periodicActions';
import { prepareMessangers } from './utils/prepareMessangers';
import { trackShowButton, trackClickChatOpen, trackClickChatClose } from './analytics';
import { isEnabledSelector } from '../../selectors/consultantCianChat';

interface IConsultantChatContainerProps {
  config: IConfig;
}

// eslint-disable-next-line import/no-default-export
export default function ConsultantChatContainer({ config }: IConsultantChatContainerProps) {
  const [ready, setReady] = React.useState(false);

  const dispatch = useDispatch();
  const accountName = useSelector(accountNameSelector);
  const chatIsOpen = useSelector(chatOpenSelector);
  const needForceOpen = useSelector(needForceOpenSelector);
  const isConsultantMessageButtonAvailable = useSelector(selectConsultantMessageButtonAvailable);
  const isBuilderChatOpened = useSelector(getBuilderChatOpened);
  const isCianChatEnabled = useSelector(isEnabledSelector);

  const webimProps = useWebimProps();
  const messangersLinks = React.useMemo(() => prepareMessangers(config), []);

  const isAvailable = Boolean(accountName && !isConsultantMessageButtonAvailable && !isBuilderChatOpened);

  const toggleOpen = React.useCallback((isOpen: boolean) => {
    dispatch(toggleChat(isOpen));

    if (isOpen) {
      trackClickChatOpen();
    } else {
      trackClickChatClose();
    }
  }, []);

  const onReady = React.useCallback(() => {
    setReady(true);
  }, []);

  React.useEffect(() => {
    if (isAvailable && !isCianChatEnabled) {
      trackShowButton();
    }
  }, [isAvailable, isCianChatEnabled]);

  React.useEffect(() => {
    if (ready && needForceOpen && isNeedToShow(CHAT_FORCE_OPEN_KEY)) {
      const timerId = setTimeout(() => {
        dispatch(toggleChat(true));
        updateShownTimestamp(CHAT_FORCE_OPEN_KEY, CHAT_FORCE_OPEN_INTERVAL);
      }, CHAT_OPEN_DELAY);

      return () => {
        clearTimeout(timerId);
      };
    }

    return undefined;
  }, [ready, needForceOpen]);

  if (!isAvailable || isCianChatEnabled) {
    return null;
  }

  return (
    <ConsultantWidgetDesktop
      webimProps={webimProps}
      chatOpened={chatIsOpen}
      messengersLinks={messangersLinks}
      toggleOpen={toggleOpen}
      onReady={onReady}
    />
  );
}
